import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useApp } from "@/store/app";
import { UserAvatar, PRESET_AVATARS, isImageAvatar } from "@/components/UserAvatar";
import { AvatarPickerTile, AvatarPreviewModal } from "@/components/AvatarPreview";


import { ModalShell } from "./leagues.index";
import { toast } from "sonner";
import { Upload, Pencil, Languages, LogOut, KeyRound, Trash2, BookOpen, ShieldCheck, Loader2, Info, ChevronsDown } from "lucide-react";
import { compressImage, MAX_ORIGINAL_BYTES } from "@/lib/imageCompression";
import { uploadAvatarDataUrl, isDataUrlAvatar } from "@/lib/avatarUpload";
import { useT, useLang } from "@/i18n";
import { FlagRO, FlagUS } from "@/components/CountryFlag";
import { AchievementsList } from "@/components/AchievementsList";
import { PredictionHistory } from "@/components/PredictionHistory";
import { useUserBadges } from "@/hooks/useUserBadges";
import { useUserStats } from "@/hooks/useUserStats";
import { signOut } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { LegalFooter } from "@/components/LegalFooter";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Link } from "@tanstack/react-router";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useLeagueByCode, useLeagueLeaderboard } from "@/hooks/useLeaguesDb";

function GlobalRankLine() {
  const t = useT();
  const user = useApp((s) => s.user)!;
  const { data: league } = useLeagueByCode("PPRO2026");
  const { data: board = [] } = useLeagueLeaderboard(league?.id);
  if (!board.length) return null;
  const idx = board.findIndex((r) => r.user_id === user.id);
  if (idx === -1) return null;
  const rank = board.filter((r) => r.points > board[idx].points).length + 1;
  return (
    <div className="mt-1.5 text-[11px] text-muted-foreground">
      🌍 {t(`Rank #${rank} of ${board.length} in Global League`, undefined, `Locul #${rank} din ${board.length} în Liga Globală`)}
    </div>
  );
}

export const Route = createFileRoute("/_app/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const user = useApp((s) => s.user)!;
  const t = useT();
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  const [resetting, setResetting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDeleteAccount() {
    setDeleting(true);
    const { error } = await supabase.rpc("delete_user" as never);
    if (error) {
      setDeleting(false);
      toast.error(error.message);
      return;
    }
    await signOut();
    toast.success(t("Account deleted", "Cont șters"));
    navigate({ to: "/login" });
  }

  async function handleResetPassword() {
    const { data } = await supabase.auth.getUser();
    const email = data.user?.email;
    if (!email) {
      toast.error(t("Email not found", "Email negăsit"));
      return;
    }
    setResetting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetting(false);
    if (error) toast.error(error.message);
    else toast.success(t("Reset email sent", "Email-ul de resetare a fost trimis"));
  }

  async function handleSignOut() {
    await signOut();
    navigate({ to: "/login" });
  }
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const setUser = useApp((s) => s.setUser);
  const predictions = useApp((s) => s.predictions);
  // All stats come from Supabase — single source of truth across devices.
  const { data: stats } = useUserStats(user.id);
  const totalPts = stats?.totalPoints ?? 0;
  const accuracyLabel =
    stats && stats.totalPredictions > 0
      ? `${stats.accuracy.toFixed(1)}%`
      : "—";
  const { data: dbBadgeIds = [] } = useUserBadges(user.id);
  const badgeCount = stats?.badgeCount ?? dbBadgeIds.length;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.username);
  const [avatar, setAvatar] = useState(user.avatar);
  const fileRef = useRef<HTMLInputElement>(null);
  const [compressing, setCompressing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  async function saveProfile() {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const fallback = name.trim().slice(0, 2).toUpperCase();
      let nextAvatar = avatar || fallback;

      // If the user picked a fresh photo, upload it to Storage first.
      if (isDataUrlAvatar(nextAvatar)) {
        nextAvatar = await uploadAvatarDataUrl(user.id, nextAvatar);
      }

      const { error } = await supabase
        .from("profiles")
        .update({ username: name.trim(), avatar: nextAvatar } as never)
        .eq("id", user.id);
      if (error) throw error;

      setUser({ ...user, username: name.trim(), avatar: nextAvatar });
      toast.success(t("Profile updated", "Perfil atualizado"));
      setEditing(false);
    } catch (e) {
      toast.error((e as Error).message || t("Could not save profile", "Nu am putut salva profilul"));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ORIGINAL_BYTES) {
      toast.error(t("Image must be under 10MB", "Imaginea trebuie să fie sub 10MB"));
      return;
    }
    setCompressing(true);
    try {
      const dataUrl = await compressImage(file, {
        maxWidth: 400,
        maxHeight: 400,
        quality: 0.85,
        square: true,
      });
      setAvatar(dataUrl);
    } catch {
      toast.error(t("Could not process image", "Nu am putut procesa imaginea"));
    } finally {
      setCompressing(false);
      e.target.value = "";
    }
  }


  return (
    <div>
      <div className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {t("Profile", "Perfil", "Profil")}
      </div>

      <div className="rounded-2xl border bg-card p-6 shadow-sm">
        {/* Centered profile header */}
        <div className="flex flex-col items-center text-center">
          <button
            onClick={() => {
              setAvatar(user.avatar);
              setName(user.username);
              setEditing(true);
            }}
            className="group relative"
            aria-label={t("Edit avatar", "Editar avatar")}
          >
            <UserAvatar
              avatar={user.avatar}
              username={user.username}
              size={96}
              rounded="rounded-full"
              isAdmin={isAdmin}
            />

            <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md ring-2 ring-card transition group-hover:scale-110">
              <Pencil className="h-3.5 w-3.5" />
            </span>
          </button>
          <div className="mt-3 text-xl font-bold">{user.username}</div>
          <GlobalRankLine />
        </div>

        {/* Single unified stats card: Points · Badges · Accuracy */}
        <div className="mt-6 grid grid-cols-3 divide-x divide-premium/30 rounded-xl border bg-background">
          <Stat label={t("Points", "Pontos", "Puncte")} value={totalPts} accent="primary" />
          <Stat label={t("Badges", "Conquistas", "Insigne")} value={badgeCount} accent="premium" />
          <Stat
            label={t("Accuracy", "Precisão", "Acuratețe")}
            value={accuracyLabel}
            accent="success"
            tooltip={t(
              "Percentage of finished matches where you correctly predicted the result (W/D/L), out of total predictions submitted.",
              "Porcentagem de partidas encerradas em que você acertou o resultado (V/E/D), do total de palpites enviados.",
              "Procentul de meciuri încheiate în care ai ghicit corect rezultatul (V/E/Î), din totalul pronosticurilor trimise."
            )}
          />
        </div>

      </div>

      <Tabs defaultValue="achievements" className="mt-6">
        <TabsList className="mb-5 grid w-full grid-cols-2">
          <TabsTrigger value="achievements">
            {t("Achievements", undefined, "Realizări")}
          </TabsTrigger>
          <TabsTrigger value="settings">
            {t("Settings", undefined, "Setări")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="achievements">
          <Link
            to="/rules"
            className="mb-3 flex w-full items-center justify-between gap-2 rounded-2xl border border-premium/60 bg-card px-4 py-3 text-sm font-semibold text-premium shadow-sm transition hover:bg-premium/5"
          >
            <span className="flex items-center gap-2">
              <span aria-hidden>📖</span>
              {t("Rules & Scoring", "Regras & Pontuação", "Reguli & Sistem de Punctaj")}
            </span>
            <span aria-hidden>›</span>
          </Link>
          <div className="rounded-2xl border bg-card p-4 shadow-sm">
            <AchievementsList memberId={user.id} predictions={predictions} earnedBadgeIds={dbBadgeIds} />
          </div>
          <PredictionHistory userId={user.id} />
        </TabsContent>

        <TabsContent value="settings">

          {/* Language — full-width control */}
          <div className="mt-3">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Languages className="h-3.5 w-3.5" />
              {t("Language", "Idioma", "Limbă")}
            </div>
            <div className="flex w-full rounded-2xl border border-input bg-card p-1 shadow-sm">
              {([
                { code: "en", label: "EN", Flag: FlagUS },
                { code: "ro", label: "RO", Flag: FlagRO },
              ] as const).map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => setLang(opt.code)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${
                    lang === opt.code
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <opt.Flag className="h-3.5 w-5 rounded-sm shadow-sm" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>


          {/* Reset password */}
          <div className="mt-3">
            <button
              onClick={handleResetPassword}
              disabled={resetting}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-input bg-card px-4 py-3 text-sm font-semibold shadow-sm transition hover:bg-accent disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4 text-primary" />
              {resetting
                ? t("Sending…", "Enviando…", "Se trimite…")
                : t("Reset password", "Redefinir senha", "Schimbă parola")}
            </button>
          </div>

          {/* Admin panel — visible only to admins */}
          {isAdmin && (
            <div className="mt-3">
              <Link
                to="/admin"
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-input bg-card px-4 py-3 text-sm font-semibold shadow-sm transition hover:bg-accent"
              >
                <ShieldCheck className="h-4 w-4 text-primary" />
                {t("Admin panel", "Panou Admin")}
              </Link>
            </div>
          )}

          {/* Sign out */}
          <div className="mt-3">
            <button
              onClick={handleSignOut}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-destructive px-4 py-3 text-sm font-semibold text-destructive-foreground shadow-sm transition hover:opacity-90"
            >
              <LogOut className="h-4 w-4" />
              {t("Sign out", "Sair", "Ieși din cont")}
            </button>
          </div>

          {/* Terms · Privacy */}
          <LegalFooter className="mt-6" />

          {/* Delete account — outlined destructive, at very bottom */}
          <div className="mt-6 mb-2">
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm font-semibold text-destructive shadow-sm transition hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              {t("Delete account", "Șterge contul")}
            </button>
          </div>
        </TabsContent>
      </Tabs>


      {confirmDelete && (
        <ModalShell
          title={t("Delete account", "Șterge contul")}
          onClose={() => (deleting ? null : setConfirmDelete(false))}
        >
          <p className="text-sm text-muted-foreground">
            {t(
              "This will permanently delete your account and all related data. This action cannot be undone.",
              "Această acțiune va șterge definitiv contul tău și toate datele asociate. Acțiunea nu poate fi anulată."
            )}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2.5 text-sm font-semibold hover:bg-accent disabled:opacity-60"
            >
              {t("Cancel", "Anulează")}
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="flex-1 rounded-lg bg-destructive px-3 py-2.5 text-sm font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-60"
            >
              {deleting
                ? t("Deleting…", "Se șterge…")
                : t("Delete", "Șterge")}
            </button>
          </div>
        </ModalShell>
      )}

      {editing && (
        <ModalShell title={t("Edit profile", "Editar perfil")} onClose={() => setEditing(false)}>
          <div className="flex max-h-[70vh] flex-col">
            <div className="flex flex-col items-center gap-3">
              <UserAvatar
                avatar={avatar}
                username={name || user.username}
                size={72}
                rounded="rounded-full"
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleUpload}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={compressing}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-1.5 text-sm font-semibold hover:bg-accent disabled:opacity-60"
                >
                  {compressing ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("Processing…", "Se procesează…")}</>
                  ) : (
                    <><Upload className="h-3.5 w-3.5" /> {t("Upload photo", "Enviar foto")}</>
                  )}
                </button>
                {isImageAvatar(avatar) && (
                  <button
                    onClick={() =>
                      setAvatar((name || user.username).slice(0, 2).toUpperCase())
                    }
                    className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                  >
                    {t("Remove", "Remover")}
                  </button>
                )}
              </div>
            </div>

            <div className="mt-3 min-h-0 flex-1 overflow-y-auto show-scrollbar pr-1">
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ChevronsDown className="h-3.5 w-3.5" />
                {t("Or pick an avatar", "Ou escolha um avatar")}
              </span>
              <div className="mx-auto mt-2 grid grid-cols-4 justify-items-center gap-3">
                {PRESET_AVATARS.map((a, i) => (
                  <AvatarPickerTile
                    key={a}
                    avatar={a}
                    selected={avatar === a}
                    onSelect={() => setAvatar(a)}
                    onPreview={() => setPreviewIndex(i)}
                  />
                ))}
              </div>
            </div>

            <div className="mt-3 shrink-0 border-t pt-3">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("Nickname", "Porecla")}
                </span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2"
                  maxLength={20}
                />
              </label>
              <button
                onClick={saveProfile}
                disabled={saving}
                className="mt-3 w-full rounded-lg bg-primary py-2.5 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                {saving ? t("Saving…", "Se salvează…") : t("Save", "Salvar")}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      <AvatarPreviewModal
        open={previewIndex !== null}
        avatars={PRESET_AVATARS}
        index={previewIndex ?? 0}
        onIndexChange={(i) => setPreviewIndex(i)}
        onSelect={(a) => setAvatar(a)}
        onClose={() => setPreviewIndex(null)}
      />

    </div>
  );
}

function Stat({
  label,
  value,
  icon,
  accent,
  tooltip,
}: {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  accent?: "primary" | "success" | "premium";
  tooltip?: string;
}) {
  const [showTip, setShowTip] = useState(false);
  const valueColor =
    accent === "primary"
      ? "text-primary"
      : accent === "success"
        ? "text-success"
        : accent === "premium"
          ? "text-premium"
          : "text-foreground";
  const iconColor =
    accent === "primary"
      ? "text-primary"
      : accent === "success"
        ? "text-success"
        : accent === "premium"
          ? "text-premium"
          : "text-muted-foreground";
  const Wrapper: React.ElementType = tooltip ? "button" : "div";
  const wrapperProps = tooltip
    ? {
        type: "button" as const,
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          setShowTip((v) => !v);
        },
        "aria-label": tooltip,
      }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`relative p-3 text-center ${tooltip ? "w-full cursor-pointer focus:outline-none" : ""}`}
    >
      {tooltip && (
        <Info className="pointer-events-none absolute right-1.5 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
      )}
      {icon && (
        <div className={`mb-1 flex justify-center ${iconColor}`}>{icon}</div>
      )}
      <div className={`text-2xl font-black tracking-tight ${valueColor}`}>
        {value}
      </div>
      <div className="mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">
        {label}
      </div>
      {tooltip && showTip && (
        <div
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-full z-50 mt-2 w-[min(15rem,calc(100vw-2rem))] rounded-lg border-2 border-premium bg-[oklch(0.15_0.05_265)] px-3 py-2 text-[11px] font-medium normal-case tracking-normal text-white shadow-xl"
        >
          <div
            aria-hidden
            className="absolute -top-1.5 right-3 h-3 w-3 rotate-45 border-l-2 border-t-2 border-premium bg-[oklch(0.15_0.05_265)]"
          />
          {tooltip}
        </div>
      )}
    </Wrapper>
  );
}


